import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { delay, race, select, take, takeEvery } from 'redux-saga/effects';
import { GlobalAction, GlobalActions, ProduktDeleted } from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { childKeyOf } from '../models/childKeys';
import { SHOP_RUN_WAIT_MS } from './pendingDeletionWait';
import { sharedDispatcher } from './sharedDispatcher';

export function* watchDeleteGroup(): SagaIterator {
  yield takeEvery(GlobalActions.PRODUKT_DELETED, safeSaga(deleteGroupSaga, App.TGV));
}

/**
 * Deleting the TGV row of the dashboard deletes the offers that were created for it, like
 * deleting each of them in the dashboard. The reducer keeps their ids for this reason.
 *
 * The children are deleted only after the shop finished its tarifierung for the deletion of the
 * group: the shop handles every deletion with takeLatest and counts the running tarifierungen.
 */
export function* deleteGroupSaga(action: GlobalAction<ProduktDeleted>): SagaIterator {
  if (!action.payload?.angebotsIds?.some(offer => offer.appId === App.TGV)) {
    return;
  }
  const { tiergruppe }: IAppState = yield select();
  const children = [
    ...Object.values(tiergruppe.angebotsIds).map(angebotsId => ({
      appId: App.TKV_V2,
      angebotsId,
    })),
    ...(tiergruppe.thhAngelegt.thhHund ? [{ appId: App.THH_HUND }] : []),
    ...(tiergruppe.thhAngelegt.thhPferd ? [{ appId: App.THH_PFERD }] : []),
  ];
  if (children.length === 0) {
    return;
  }
  yield race({
    finished: take([GlobalActions.TARIFIERE_SHOP_SUCCESS, GlobalActions.TARIFIERE_SHOP_FAILED]),
    timeout: delay(SHOP_RUN_WAIT_MS),
  });
  const { transientState }: IAppState = yield select();
  const stillPending = children.filter(child => {
    const key = childKeyOf(child);
    return key !== null && transientState.children.pendingDeletion.includes(key);
  });
  if (stillPending.length > 0) {
    sharedDispatcher().dispatch(new GlobalActions().produktDeleted(stillPending));
  }
}
